import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  Plus, Sparkles, Trash2, Calendar, Settings,
  Image as ImageIcon, Palette, GripVertical, Move, ChevronLeft, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import Header from "../components/Header";
import CardModal from "../components/CardModal";
import AISummaryModal from "../components/AISummaryModal";
import BoardSettings from "../components/BoardSettings";
import ImageLightbox from "../components/ImageLightbox";
import MoveCardMenu from "../components/MoveCardMenu";
import { BoardViewSkeleton } from "../components/Skeletons";
import MobileKanbanHint from "../components/MobileKanbanHint";
import { LIST_COLORS, listColorStyle } from "../lib/colors";

export default function BoardView() {
  const { boardId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCardId, setActiveCardId] = useState(null);
  const [showAI, setShowAI] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [addingList, setAddingList] = useState(false);
  const [newListTitle, setNewListTitle] = useState("");
  const [collapsedLists, setCollapsedLists] = useState({});
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [quickMoveCard, setQuickMoveCard] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get(`/boards/${boardId}`); setData(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat papan"); }
    finally { setLoading(false); }
  }, [boardId]);

  useEffect(() => { load(); }, [load]);

  const board = data?.board;
  const lists = useMemo(() => (data?.lists || []).slice().sort((a, b) => a.position - b.position), [data]);
  const cards = useMemo(() => (data?.cards || []).slice().sort((a, b) => a.position - b.position), [data]);
  const labels = data?.labels || [];
  const members = data?.members || [];
  const myRole = data?.my_role;
  const canEdit = myRole && myRole !== "viewer";
  const isSpv = myRole === "spv" || myRole === "master";

  const cardsByList = (listId) => cards.filter((c) => c.list_id === listId);
  const activeCard = cards.find((c) => c.card_id === activeCardId);
  const updateLocal = (mutator) => setData((d) => mutator({ ...d }));

  const onDragEnd = async (result) => {
    const { destination, source, draggableId, type } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    if (type === "LIST") {
      const newLists = [...lists];
      const [moved] = newLists.splice(source.index, 1);
      newLists.splice(destination.index, 0, moved);
      const newIdx = destination.index;
      let newPos;
      if (newIdx === 0) newPos = newLists[1] ? newLists[1].position - 1024 : 1024;
      else if (newIdx >= newLists.length - 1) newPos = newLists[newLists.length - 2].position + 1024;
      else newPos = (newLists[newIdx - 1].position + newLists[newIdx + 1].position) / 2;
      const updated = newLists.map((l) => l.list_id === moved.list_id ? { ...l, position: newPos } : l);
      updateLocal((d) => { d.lists = updated; return d; });
      try { await api.post("/lists/reorder", { list_id: moved.list_id, new_position: newPos }); }
      catch { toast.error("Gagal urutkan daftar"); load(); }
      return;
    }

    const moved = cards.find((c) => c.card_id === draggableId);
    if (!moved) return;
    const otherCards = cards.filter((c) => c.card_id !== draggableId);
    const targetCards = otherCards.filter((c) => c.list_id === destination.droppableId).sort((a, b) => a.position - b.position);
    targetCards.splice(destination.index, 0, { ...moved, list_id: destination.droppableId });
    let newPos;
    if (targetCards.length === 1) newPos = 1024;
    else if (destination.index === 0) newPos = targetCards[1].position - 1024;
    else if (destination.index >= targetCards.length - 1) newPos = targetCards[targetCards.length - 2].position + 1024;
    else newPos = (targetCards[destination.index - 1].position + targetCards[destination.index + 1].position) / 2;

    updateLocal((d) => {
      d.cards = d.cards.map((c) => c.card_id === draggableId ? { ...c, list_id: destination.droppableId, position: newPos } : c);
      return d;
    });
    try { await api.post("/cards/move", { card_id: draggableId, target_list_id: destination.droppableId, new_position: destination.index }); }
    catch { toast.error("Gagal memindahkan"); load(); }
  };

  const addCard = async (listId, title, onBehalfOf = null) => {
    if (!title.trim()) return;
    try {
      const r = await api.post("/cards", { list_id: listId, title: title.trim(), on_behalf_of: onBehalfOf });
      updateLocal((d) => { d.cards = [...d.cards, r.data]; return d; });
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const addList = async () => {
    if (!newListTitle.trim()) { setAddingList(false); return; }
    try {
      const r = await api.post(`/boards/${boardId}/lists`, { title: newListTitle.trim().toUpperCase() });
      updateLocal((d) => { d.lists = [...d.lists, r.data]; return d; });
      setNewListTitle(""); setAddingList(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const deleteList = async (listId) => {
    if (!window.confirm("Hapus daftar dan semua kartunya?")) return;
    try {
      await api.delete(`/lists/${listId}`);
      updateLocal((d) => {
        d.lists = d.lists.filter((l) => l.list_id !== listId);
        d.cards = d.cards.filter((c) => c.list_id !== listId);
        return d;
      });
      toast.success("Daftar dihapus");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const renameList = async (listId, newTitle) => {
    const t = newTitle.trim();
    if (!t) return;
    updateLocal((d) => { d.lists = d.lists.map((l) => l.list_id === listId ? { ...l, title: t } : l); return d; });
    try { await api.patch(`/lists/${listId}`, { title: t }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); load(); }
  };

  const updateListColor = async (listId, color) => {
    updateLocal((d) => { d.lists = d.lists.map((l) => l.list_id === listId ? { ...l, color } : l); return d; });
    try { await api.patch(`/lists/${listId}`, { color }); } catch { load(); }
  };

  const moveCardTo = async (cardId, targetListId) => {
    try {
      await api.post("/cards/move", { card_id: cardId, target_list_id: targetListId, new_position: 9999 });
      setQuickMoveCard(null);
      toast.success("Kartu dipindahkan");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  if (loading) return <BoardViewSkeleton />;
  if (!data) return (<div className="min-h-screen bg-white"><Header /><div className="p-10 font-mono">Papan tidak ditemukan. <Link className="underline" to="/dashboard">Kembali</Link></div></div>);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const bgStyle = (() => {
    if (board.background_type === "color" && board.background_value) return { background: board.background_value };
    if (board.background_type === "gradient" && board.background_value) return { background: board.background_value };
    if (board.background_type === "image" && board.background_value) {
      return { backgroundImage: `url(${board.background_value})`, backgroundSize: "cover", backgroundPosition: "center" };
    }
    return { background: "#F9F9F9" };
  })();

  return (
    <div className="h-[100dvh] flex flex-col bg-white overflow-hidden">
      <Header
        breadcrumb={
          <>
            <Link to="/dashboard" className="hover:underline">DASBOR</Link>
            <span className="text-black/40">/</span>
            <span className="text-black truncate max-w-[200px]" data-testid="board-title">{board.title}</span>
          </>
        }
      >
        <button onClick={() => setShowSettings(true)} data-testid="board-settings-btn"
          className="bg-white border-2 border-black px-3 py-2.5 font-mono text-xs uppercase tracking-wider btn-brut hover:bg-canary hover:-translate-y-0.5 hover:shadow-brut flex items-center gap-2">
          <Settings size={14} strokeWidth={2.5} /> <span className="hidden md:inline">Pengaturan</span>
        </button>
        <button onClick={() => setShowAI(true)} data-testid="ai-summary-btn"
          className="bg-black text-white border-2 border-black px-4 py-2.5 font-mono text-xs uppercase tracking-wider btn-brut hover:bg-klein hover:-translate-y-0.5 hover:shadow-brut flex items-center gap-2">
          <Sparkles size={14} strokeWidth={2.5} />
          <span className="hidden md:inline">Ringkasan AI</span>
        </button>
      </Header>

      <div className="border-b border-black bg-[#F9F9F9] px-3 md:px-6 py-2 md:py-3 flex items-center justify-between flex-wrap gap-2 md:gap-3">
        <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1">
          <div className="hidden md:block font-mono text-[10px] uppercase tracking-widest text-black/50">[ PAPAN ]</div>
          <h1 className="font-heading font-black text-lg md:text-2xl uppercase tracking-tight truncate max-w-[58vw] md:max-w-none min-w-0" data-testid="board-heading">{board.title}</h1>
          <span className="font-mono text-[8px] md:text-[9px] uppercase tracking-widest bg-black text-white px-1.5 md:px-2 py-0.5 md:py-1">{myRole}</span>
          <div className="hidden md:flex -space-x-2">
            {members.slice(0, 5).map((m) => (<Avatar key={m.user_id} user={m.user} title={`${m.user?.name} (${m.role})`} />))}
            {members.length > 5 && (<div className="w-8 h-8 bg-black text-white border-2 border-white font-mono text-[10px] flex items-center justify-center font-bold">+{members.length - 5}</div>)}
          </div>
        </div>
        <div className="font-mono text-[9px] md:text-[10px] uppercase tracking-widest text-black/60 flex gap-3 md:gap-6 shrink-0">
          <div><span className="text-black/40">Daftar:</span> <span className="text-black font-bold">{lists.length}</span></div>
          <div><span className="text-black/40">Kartu:</span> <span className="text-black font-bold">{cards.length}</span></div>
        </div>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="board" type="LIST" direction="horizontal">
          {(prov) => (
            <div ref={prov.innerRef} {...prov.droppableProps}
              className="flex-1 overflow-x-auto overflow-y-hidden touch-pan-x"
              style={bgStyle} data-testid="kanban-area">
              <div className="flex h-full items-start" style={{ minWidth: 'max-content' }}>
                {lists.map((list, idx) => (
                  <Draggable key={list.list_id} draggableId={list.list_id} index={idx} isDragDisabled={!canEdit || isMobile}>
                    {(p, snap) => (
                      <div ref={p.innerRef} {...p.draggableProps}
                        className={`${snap.isDragging ? "opacity-90" : ""}`}>
                        <ListColumn
                          list={list}
                          cards={cardsByList(list.list_id)}
                          labels={labels} members={members}
                          onAddCard={addCard}
                          onDeleteList={() => deleteList(list.list_id)}
                          onRenameList={(t) => renameList(list.list_id, t)}
                          onOpenCard={(c) => setActiveCardId(c.card_id)}
                          onUpdateColor={(color) => updateListColor(list.list_id, color)}
                          onQuickMove={(c) => setQuickMoveCard(c)}
                          quickMoveCardId={quickMoveCard?.card_id}
                          onCloseMoveMenu={() => setQuickMoveCard(null)}
                          onMoveCardTo={moveCardTo}
                          onOpenLightbox={(src) => setLightboxSrc(src)}
                          idx={idx}
                          canEdit={canEdit} isSpv={isSpv}
                          dragHandleProps={p.dragHandleProps}
                          collapsed={!!collapsedLists[list.list_id]}
                          onToggleCollapse={() => setCollapsedLists((c) => ({ ...c, [list.list_id]: !c[list.list_id] }))}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {prov.placeholder}
                {canEdit && (
                  <AddListColumn adding={addingList} setAdding={setAddingList}
                    value={newListTitle} setValue={setNewListTitle} onSubmit={addList} />
                )}
              </div>
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <MobileKanbanHint listCount={lists.length} />

      {activeCard && (
        <CardModal card={activeCard} board={board} lists={lists} labels={labels} members={members}
          myRole={myRole} onClose={() => setActiveCardId(null)} onChanged={() => load()}
          onOpenLightbox={(src) => setLightboxSrc(src)} />
      )}
      {showAI && <AISummaryModal boardId={boardId} onClose={() => setShowAI(false)} />}
      {showSettings && (
        <BoardSettings board={board} labels={labels} members={members} lists={lists} myRole={myRole}
          onClose={() => setShowSettings(false)} onChanged={() => load()} />
      )}
      {lightboxSrc && <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </div>
  );
}

function Avatar({ user, title, size = 32 }) {
  if (!user) return null;
  return user.picture ? (
    <img src={user.picture} alt="" title={title} style={{ width: size, height: size }} className="border-2 border-white object-cover" />
  ) : (
    <div style={{ width: size, height: size }} title={title} className="bg-canary border-2 border-white flex items-center justify-center font-mono text-[10px] font-bold">
      {(user.name || "?").slice(0, 2).toUpperCase()}
    </div>
  );
}

function ListColumn(props) {
  const {
    list, cards, labels, members, onAddCard, onDeleteList, onRenameList, onOpenCard, onUpdateColor,
    onQuickMove, quickMoveCardId, onCloseMoveMenu, onMoveCardTo, onOpenLightbox,
    idx, canEdit, isSpv, dragHandleProps, collapsed, onToggleCollapse,
  } = props;
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [onBehalfOf, setOnBehalfOf] = useState("");
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(list.title);
  const colorStyle = listColorStyle(list.color);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const stop = (e) => { e.stopPropagation(); };

  // ============ COLLAPSED VIEW ============
  if (collapsed) {
    return (
      <div className="w-12 flex-shrink-0 border-r border-black bg-white animate-blockIn cursor-pointer"
        style={{ height: "var(--kanban-height, calc(100vh - 144px))" }}
        onClick={onToggleCollapse}
        data-testid={`list-collapsed-${list.list_id}`}
        title={`${list.title} (klik untuk buka)`}>
        <div className="h-full flex flex-col items-center py-3 gap-3" style={colorStyle}>
          <div className="font-mono text-[10px] tracking-widest opacity-60">#{String(idx + 1).padStart(2, "0")}</div>
          <ChevronRight size={14} strokeWidth={2.5} />
          <div className="font-mono text-2xl font-black tabular-nums">{cards.length}</div>
          <div className="flex-1 flex items-center justify-center min-h-0">
            <div className="font-heading font-bold text-base uppercase tracking-tight"
              style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", fontStyle: "italic" }}>
              {list.title}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-[88vw] max-w-[22rem] md:w-80 md:max-w-none flex-shrink-0 flex flex-col border-r border-black bg-white animate-blockIn"
      style={{ height: "var(--kanban-height, calc(100vh - 144px))" }}
      data-testid={`list-${list.list_id}`}>
      <div className="p-2.5 md:p-3 border-b border-black flex items-center justify-between gap-2 sticky top-0 overflow-hidden" style={colorStyle}>
        {/* Drag handle (narrow) */}
        {canEdit && (
          <div {...(dragHandleProps || {})} className="cursor-grab active:cursor-grabbing pr-1 opacity-50 hover:opacity-100 shrink-0"
            data-testid={`drag-list-${list.list_id}`} title="Seret untuk pindah">
            <GripVertical size={14} strokeWidth={2.5} />
          </div>
        )}
        <div className="flex items-center gap-1.5 md:gap-2 min-w-0 flex-1">
          <span className="font-mono text-[10px] tracking-widest opacity-60 shrink-0">#{String(idx + 1).padStart(2, "0")}</span>
          {editingTitle && isSpv ? (
            <input autoFocus value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={() => { setEditingTitle(false); if (titleDraft !== list.title) onRenameList(titleDraft); }}
              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") { setTitleDraft(list.title); setEditingTitle(false); } }}
              onMouseDown={stop}
              data-testid={`rename-list-${list.list_id}`}
              className="min-w-0 flex-1 bg-white border border-black px-1 font-heading font-bold uppercase tracking-tight" />
          ) : (
            <h3 onClick={() => isSpv && setEditingTitle(true)}
              className={`min-w-0 flex-1 font-heading font-bold text-sm md:text-base uppercase tracking-tight truncate ${isSpv ? "cursor-text" : ""}`}
              title={isSpv ? "Klik untuk ubah nama (hanya SPV/MASTER)" : ""}>{list.title}</h3>
          )}
          <span className="font-mono text-[10px] bg-black text-white px-1.5 py-0.5 shrink-0">{cards.length}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onMouseDown={stop} onClick={(e) => { stop(e); onToggleCollapse(); }} title="Lipat"
            data-testid={`collapse-list-${list.list_id}`}
            className="w-8 h-8 md:w-7 md:h-7 flex items-center justify-center border border-transparent hover:border-black bg-white hover:bg-mist transition-colors touch-manipulation">
            <ChevronLeft size={12} strokeWidth={2.5} />
          </button>
          {canEdit && (
            <button onMouseDown={stop} onClick={(e) => { stop(e); setShowColorPicker(!showColorPicker); }} title="Warna" data-testid={`list-color-${list.list_id}`}
              className="w-8 h-8 md:w-7 md:h-7 flex items-center justify-center border border-transparent hover:border-black bg-white hover:bg-canary transition-colors touch-manipulation">
              <Palette size={12} strokeWidth={2.5} />
            </button>
          )}
          {isSpv && (
            <button onMouseDown={stop} onClick={(e) => { stop(e); onDeleteList(); }} data-testid={`delete-list-${list.list_id}`} title="Hapus"
              className="w-8 h-8 md:w-7 md:h-7 flex items-center justify-center border border-transparent hover:border-black bg-white hover:bg-signal hover:text-white transition-colors touch-manipulation">
              <Trash2 size={12} strokeWidth={2.5} />
            </button>
          )}
        </div>
      </div>
      {showColorPicker && (
        <div className="border-b border-black p-2 bg-white grid grid-cols-6 gap-1 animate-slideUp">
          {LIST_COLORS.map((c) => (
            <button key={c.id} onClick={() => { onUpdateColor(c.id); setShowColorPicker(false); }}
              title={c.label} data-testid={`color-${c.id}`}
              className="h-8 border border-black btn-brut hover:-translate-y-0.5 hover:shadow-brut"
              style={{ background: c.bg }} />
          ))}
          <button onClick={() => { onUpdateColor(null); setShowColorPicker(false); }}
            title="Tanpa warna" data-testid="color-none"
            className="h-8 border border-black btn-brut hover:-translate-y-0.5 hover:shadow-brut bg-white font-mono text-[10px]">✕</button>
        </div>
      )}

      <Droppable droppableId={list.list_id} type="CARD">
        {(provided, snapshot) => (
          <div ref={provided.innerRef} {...provided.droppableProps}
            className={`flex-1 overflow-y-auto p-2 space-y-2 ${snapshot.isDraggingOver ? "drop-target" : ""}`}>
            {cards.map((card, index) => (
              <Draggable draggableId={card.card_id} index={index} key={card.card_id} isDragDisabled={!canEdit || isMobile}>
                {(p, snap) => (
                  <div ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps}
                    onClick={() => !snap.isDragging && onOpenCard(card)}
                    data-testid={`card-${card.card_id}`}
                    className={`group relative bg-white border border-black overflow-hidden ${isMobile ? '' : 'cursor-grab active:cursor-grabbing hover:-translate-y-0.5 hover:shadow-brut'} transition-all ${snap.isDragging ? "shadow-brutDrag rotate-[-1.5deg]" : ""}`}>
                    {card.cover_url && (
                      <div className="h-28 border-b border-black overflow-hidden relative">
                        <img src={card.cover_url} alt=""
                          onClick={(e) => { e.stopPropagation(); onOpenLightbox(card.cover_url); }}
                          className="w-full h-full object-cover cursor-zoom-in" />
                      </div>
                    )}
                    {/* Quick-move button (always top-right, overlays cover) */}
                    {canEdit && (
                      <button onMouseDown={stop} onClick={(e) => { e.stopPropagation(); onQuickMove(card); }}
                        data-testid={`quick-move-${card.card_id}`}
                        title="Pindah kartu"
                        className="absolute top-1.5 right-1.5 w-8 h-8 md:w-7 md:h-7 bg-white border border-black flex items-center justify-center hover:bg-klein hover:text-white opacity-90 hover:opacity-100 transition-all touch-manipulation">
                        <Move size={11} strokeWidth={2.5} />
                      </button>
                    )}
                    {/* Move menu for this card */}
                    {quickMoveCardId === card.card_id && (
                      <div onClick={stop} onMouseDown={stop}>
                        <MoveCardMenu currentListId={card.list_id}
                          onMove={(targetListId) => onMoveCardTo(card.card_id, targetListId)}
                          onClose={onCloseMoveMenu} />
                      </div>
                    )}
                    <div className="p-3 space-y-2">
                      {card.label_ids && card.label_ids.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {card.label_ids.map((lid) => {
                            const lbl = labels.find((l) => l.label_id === lid);
                            if (!lbl) return null;
                            return (
                              <span key={lid} className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-black"
                                style={{ background: lbl.color, color: getContrast(lbl.color) }}>{lbl.name}</span>
                            );
                          })}
                        </div>
                      )}
                      <div className="font-bold text-sm leading-snug">{card.title}</div>
                      {card.description && (
                        <div className="font-mono text-[11px] text-black/60 line-clamp-2"
                          dangerouslySetInnerHTML={{ __html: stripTags(card.description).slice(0, 120) }} />
                      )}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-black/60">
                          {card.due_date && (<span className="flex items-center gap-1"><Calendar size={10} strokeWidth={2.5} /> {card.due_date}</span>)}
                          {card.attachments && card.attachments.length > 0 && (<span className="flex items-center gap-1"><ImageIcon size={10} strokeWidth={2.5} /> {card.attachments.length}</span>)}
                        </div>
                        {card.members && card.members.length > 0 && (
                          <div className="flex -space-x-1.5">
                            {card.members.slice(0, 3).map((m) => (<Avatar key={m.user_id} user={m} size={20} title={m.name} />))}
                            {card.members.length > 3 && (<div className="w-5 h-5 bg-black text-white border-2 border-white font-mono text-[8px] flex items-center justify-center font-bold">+{card.members.length - 3}</div>)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>

      {canEdit && (
        <div className="border-t border-black p-2 bg-white">
          {adding ? (
            <div className="space-y-2">
              <textarea autoFocus value={title} onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onAddCard(list.list_id, title, onBehalfOf || null); setTitle(""); setAdding(false); }
                  if (e.key === "Escape") setAdding(false);
                }}
                data-testid={`new-card-input-${list.list_id}`}
                placeholder="Tulis kartu…" rows={2}
                className="w-full bg-white border border-black p-2 font-mono text-xs focus:shadow-brutBlue transition-all resize-none" />
              {isSpv && members.length > 1 && (
                <select value={onBehalfOf} onChange={(e) => setOnBehalfOf(e.target.value)}
                  className="w-full bg-white border border-black p-1 font-mono text-[10px] uppercase tracking-wider">
                  <option value="">Atas nama: (diri sendiri)</option>
                  {members.map((m) => (<option key={m.user_id} value={m.user_id}>{m.user?.name || m.user?.email}</option>))}
                </select>
              )}
              <div className="flex gap-2">
                <button onClick={() => { onAddCard(list.list_id, title, onBehalfOf || null); setTitle(""); setAdding(false); }}
                  data-testid={`confirm-card-${list.list_id}`}
                  className="bg-klein text-white border border-black px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider btn-brut hover:bg-black">Tambah</button>
                <button onClick={() => { setAdding(false); setTitle(""); }}
                  className="text-black/60 font-mono text-[10px] uppercase tracking-wider hover:text-black">Batal</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAdding(true)} data-testid={`add-card-${list.list_id}`}
              className="w-full flex items-center justify-center gap-2 py-2 font-mono text-[11px] uppercase tracking-widest text-black/60 hover:bg-black hover:text-white border border-transparent hover:border-black transition-colors">
              <Plus size={12} strokeWidth={3} /> Tambah Kartu
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function AddListColumn({ adding, setAdding, value, setValue, onSubmit }) {
  return (
    <div className="w-[88vw] max-w-[22rem] md:w-80 md:max-w-none flex-shrink-0 flex flex-col p-3">
      {adding ? (
        <div className="border-2 border-black bg-white p-3 space-y-2 animate-blockIn">
          <input autoFocus value={value} onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") onSubmit(); if (e.key === "Escape") { setAdding(false); setValue(""); } }}
            data-testid="new-list-input" placeholder="Nama daftar…"
            className="w-full border border-black p-2 font-mono text-xs uppercase tracking-wider focus:shadow-brutBlue transition-all" />
          <div className="flex gap-2">
            <button onClick={onSubmit} data-testid="confirm-new-list"
              className="bg-klein text-white border border-black px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider btn-brut hover:bg-black">Tambah</button>
            <button onClick={() => { setAdding(false); setValue(""); }}
              className="text-black/60 font-mono text-[10px] uppercase tracking-wider hover:text-black">Batal</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} data-testid="add-list-btn"
          className="border-2 border-dashed border-black/40 hover:border-black bg-white/70 backdrop-blur-sm py-3 flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-widest text-black/60 hover:text-black btn-brut hover:-translate-y-0.5 hover:shadow-brut">
          <Plus size={14} strokeWidth={3} /> Tambah Daftar
        </button>
      )}
    </div>
  );
}

function stripTags(html) { if (!html) return ""; return html.replace(/<[^>]+>/g, " "); }
function getContrast(hex) {
  if (!hex) return "#000";
  const c = hex.replace("#", "");
  const r = parseInt(c.slice(0, 2), 16), g = parseInt(c.slice(2, 4), 16), b = parseInt(c.slice(4, 6), 16);
  return (r * 0.299 + g * 0.587 + b * 0.114) > 170 ? "#000" : "#fff";
}
